import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { seatingPlansSubscription } from "@/lib/firestore/subscriptions/seating-plans";
import { NO_WEDDING_KEY } from "@/lib/firestore/subscription-keys";
import {
  EMPTY_SEATING_PLANS,
  SEATING_PLANS_SNAPSHOT_EMPTY,
  SEATING_PLANS_SNAPSHOT_LOADING,
} from "@/lib/firestore/subscription-snapshots";
import { useWedding } from "./useWedding";
import { useGuests } from "./useGuests";
import type { SeatingPlan } from "@amazing-wedding/shared-types";
import {
  clearStaleSeatingMigrationLock,
  ensureDefaultSeatingPlan,
  migrateLegacySeatingIfNeeded,
  setActiveSeatingPlan,
} from "@/features/seating/plan-operations";
import { defaultPlanName as defaultSeatingPlanName } from "@/features/seating/seating-defaults";
import { seatingErrorMessage } from "@/features/seating/seating-errors";

function activePlanResolved(activePlanId: string | null, plans: SeatingPlan[]): boolean {
  return activePlanId != null && plans.some((p) => p.id === activePlanId);
}

/** Client-side plan id before wedding.activeSeatingPlanId catches up on snapshot. */
function resolveActivePlanId(
  activePlanId: string | null,
  plans: SeatingPlan[],
  optimisticPlanId: string | null,
): string | null {
  if (activePlanId != null && plans.some((p) => p.id === activePlanId)) {
    return activePlanId;
  }
  if (plans.length > 0 && activePlanId == null) {
    return plans[0]!.id;
  }
  if (plans.length === 1) {
    return plans[0]!.id;
  }
  return activePlanId ?? optimisticPlanId;
}

interface UseSeatingPlansResult {
  plans: SeatingPlan[];
  loading: boolean;
  migrating: boolean;
}

export function useSeatingPlans(): UseSeatingPlansResult {
  const { t } = useTranslation();
  const { wedding, loading: weddingLoading } = useWedding();
  const weddingId = wedding?.id;
  const seatingMigrationDone = wedding?.seatingMigrationDone;
  const { guests, loading: guestsLoading } = useGuests();
  const [migrating, setMigrating] = useState(false);
  const weddingRef = useRef(wedding);
  weddingRef.current = wedding;
  const guestsRef = useRef(guests);
  guestsRef.current = guests;

  const key = weddingId ?? NO_WEDDING_KEY;

  const subscribe = useCallback(
    (listener: () => void) => {
      if (!weddingId || weddingLoading) return () => {};
      return seatingPlansSubscription.subscribe(key, listener);
    },
    [weddingId, weddingLoading, key],
  );

  const getSnapshot = useCallback(() => {
    if (weddingLoading) return SEATING_PLANS_SNAPSHOT_LOADING;
    if (!weddingId) return SEATING_PLANS_SNAPSHOT_EMPTY;
    return seatingPlansSubscription.getSnapshot(key);
  }, [weddingId, weddingLoading, key]);

  const snapshot = useSyncExternalStore(
    subscribe,
    getSnapshot,
    () => SEATING_PLANS_SNAPSHOT_LOADING,
  );

  useEffect(() => {
    if (!weddingId || seatingMigrationDone || guestsLoading) return;

    let cancelled = false;
    (async () => {
      setMigrating(true);
      try {
        const currentWedding = weddingRef.current;
        if (!currentWedding) return;
        await clearStaleSeatingMigrationLock(currentWedding);
        await migrateLegacySeatingIfNeeded(
          currentWedding,
          guestsRef.current,
          defaultSeatingPlanName(t),
        );
      } catch {
        // Migration may fail if rules not deployed — ensureDefault handles empty state.
      } finally {
        if (!cancelled) setMigrating(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [weddingId, seatingMigrationDone, guestsLoading, t]);

  if (weddingLoading) {
    return { plans: EMPTY_SEATING_PLANS, loading: true, migrating };
  }
  if (!weddingId) {
    return { plans: EMPTY_SEATING_PLANS, loading: false, migrating };
  }

  return { plans: snapshot.data, loading: snapshot.loading, migrating };
}

interface UseActiveSeatingPlanResult {
  activePlanId: string | null;
  activePlan: SeatingPlan | null;
  legacyPlanId: string | null | undefined;
  recovering: boolean;
  /** User-facing message when ensureDefaultSeatingPlan / setActive fails. */
  recoveryError: string | null;
}

/**
 * Resolves the active seating plan and auto-recovers when activeSeatingPlanId is stale.
 */
export function useActiveSeatingPlan(
  plans: SeatingPlan[],
  plansLoading: boolean,
): UseActiveSeatingPlanResult {
  const { t } = useTranslation();
  const { wedding } = useWedding();
  const weddingId = wedding?.id;
  const weddingRef = useRef(wedding);
  weddingRef.current = wedding;
  const [recovering, setRecovering] = useState(false);
  const [recoveryError, setRecoveryError] = useState<string | null>(null);
  const [optimisticPlanId, setOptimisticPlanId] = useState<string | null>(null);

  const activePlanId = wedding?.activeSeatingPlanId ?? null;
  const legacyPlanId = wedding?.legacySeatingPlanId;
  const activePlan = plans.find((p) => p.id === activePlanId) ?? null;

  useEffect(() => {
    setOptimisticPlanId(null);
    setRecoveryError(null);
  }, [weddingId]);

  useEffect(() => {
    if (!weddingId || plansLoading) return;
    if (activePlanResolved(activePlanId, plans)) {
      setRecoveryError(null);
      setOptimisticPlanId(null);
      return;
    }
    if (recoveryError) return;
    if (optimisticPlanId != null) return;

    let cancelled = false;
    (async () => {
      setRecovering(true);
      setRecoveryError(null);
      try {
        const currentWedding = weddingRef.current;
        if (!currentWedding) return;
        const id = await ensureDefaultSeatingPlan(currentWedding, defaultSeatingPlanName(t));
        if (!cancelled) setOptimisticPlanId(id);
        if (activePlanId !== id) {
          await setActiveSeatingPlan(weddingId, id);
        }
        if (!cancelled) setRecoveryError(null);
      } catch (err) {
        if (!cancelled) {
          setOptimisticPlanId(null);
          const detail = seatingErrorMessage(err, t);
          setRecoveryError(
            detail === t("seating.errors.generic")
              ? t("seating.screen.planRecoveryFailed")
              : detail,
          );
        }
      } finally {
        if (!cancelled) setRecovering(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [weddingId, activePlanId, plans, plansLoading, recoveryError, optimisticPlanId, t]);

  useEffect(() => {
    if (!weddingId || plansLoading || plans.length === 0) return;
    if (activePlanId && plans.some((p) => p.id === activePlanId)) return;
    const fallbackId = plans[0]!.id;
    void setActiveSeatingPlan(weddingId, fallbackId);
  }, [weddingId, activePlanId, plans, plansLoading]);

  const resolvedPlanId = resolveActivePlanId(activePlanId, plans, optimisticPlanId);
  const resolvedPlan =
    plans.find((p) => p.id === resolvedPlanId) ?? (plans.length === 1 ? plans[0]! : null);

  return {
    activePlanId: resolvedPlanId,
    activePlan: activePlan ?? resolvedPlan,
    legacyPlanId,
    recovering,
    recoveryError,
  };
}
